var RefineService_1;
var _a;
import { __decorate, __metadata, __param } from "tslib";
import { i18nSymbol } from '@/i18n';
import formatLeftFilterConfig from '@/infrastructure/utils/formatLeftFilterConfig';
import { inject, injectService, provide } from '@/inversify.config';
import { DetailServiceSymbol } from '@/services/DetailService';
import { workspaceApiUrlPrefix } from '@cores/constants/envConfig';
import filterRankingData from '@cores/utils/filterRankingData';
import psHttp, { RequestChannel } from '@patsnap/common-ps-http';
import { BASIC_TYPE, FIELD_TYPES } from 'library-@/left-refine-filter/config/fieldType';
import _ from 'lodash';
import Service from './Service';
import { SmartFilterRepository } from '@/domains/hiro/repositories/smart-filter-repository';
import { useSmartFilter } from '@/domains/hiro/composables/use-smart-filter';
import { PN_STR } from 'library-@/constants/index';
const initSrpFetchChannel = new RequestChannel();
// 左侧过滤init
function leftFilterInitApi(data = {}) {
    return initSrpFetchChannel.requestAsPromise({
        url: `${workspaceApiUrlPrefix}/filter/srp-init`,
        method: 'POST',
        data,
    }).then(resp => {
        // @ts-ignore todo
        return resp.data;
    });
}
// 左侧过滤ranking
function getLeftFilterRankingApi(data = {}) {
    return psHttp({
        url: `${workspaceApiUrlPrefix}/filter/ranking`,
        method: 'POST',
        data,
    });
}
// pum 特征属性获取单位
function measurementApi(data = {}) {
    return initSrpFetchChannel.requestAsPromise({
        url: `${workspaceApiUrlPrefix}/filter/property/measurement`,
        method: 'POST',
        data,
    });
}
// pum 重新获取单位和初始值
function unitConvertApi(data) {
    return initSrpFetchChannel.requestAsPromise({
        url: `${workspaceApiUrlPrefix}/filter/property/unit/convert`,
        method: 'POST',
        data,
    });
}
const { get, set, merge, compact, forEach, find, map, cloneDeep, filter } = _;
// from packages/analytics-next/src/apis/search-result/filter-init.ts
// PS：tooltip的文案在组件库内部
const TOOLTIP_FIELDS = {
    SIMPLE_LEGAL_STATUS: 'snapBusiness.filter.SIMPLE_LEGAL_STATUS',
    EFAM_COUNTRY: 'snapBusiness.filter.EFAM_COUNTRY',
    FAM_COUNTRY: 'snapBusiness.filter.FAM_COUNTRY',
    SEP_SOURCE: 'snapBusiness.filter.SEP_SOURCE',
    PATENT_TYPE: 'snapBusiness.filter.PATENT_TYPE',
    ANCS: 'snapBusiness.filter.ANCS',
    CPC_ALL_FACET: 'snapBusiness.filter.CPC_ALL_FACET',
    TECH_DOMAIN: 'snapBusiness.filter.TECH_DOMAIN',
    BENEFIT_TP_ID: 'snapBusiness.filter.BENEFIT_TP_ID',
};
// efq的各种不同类型的filter 拼接
function externalFieldsQueries2EfqString(externalFieldsQueries) {
    let efqString = '';
    externalFieldsQueries.forEach(item => {
        const { value, field, childField } = item;
        if (!value.length)
            return;
        let efqItem = '';
        if (FIELD_TYPES[field] === BASIC_TYPE.pum) {
            const efqItemValueString = compact(value)
                .map((item) => {
                const min = get(item, 'pum.measurement.min_value') || '';
                const max = get(item, 'pum.measurement.max_value') || '';
                // @ts-ignore
                if (min && max) {
                    return `(${field}:"${item.name.replace(/"/g, '\\"')}" AND PUM_VALUE:[${min} TO ${max}])`;
                }
                return `(${field}:"${item.name.replace(/"/g, '\\"')}")`;
            }).join(' OR ');
            efqItem = `MATERIAL:(${efqItemValueString})`;
        }
        else if (FIELD_TYPES[field] === BASIC_TYPE.checkRange) {
            /**
             * vc-金额、投资方规模
             * 形如(money_amount:[1 TO 1000000}) | (money_amount:[10000001 TO 25000000} OR money_amount:[250000001 TO *])
             * 后面的是数字则 } ,否则 ]
             */
            efqItem = `(${compact(value)
                .map((item) => {
                const name = item.name && item.name.split(',');
                // @ts-ignore
                return `${field}:[${name[0]} TO ${name[1]}${isNaN(name[1]) ? ']' : '}'}`;
            })
                .join(' OR ')})`;
        }
        else if (value.every(item => typeof item === 'object')) {
            const efqItemValueString = compact(value)
                // 需要加上引号
                .map((item) => `"${item.name.replace(/"/g, '\\"')}"`)
                .join(' OR ');
            efqItem = `${childField || field}:(${efqItemValueString})`;
        }
        else {
            const [min, max] = value;
            efqItem = `${field}:[${min} TO ${max}]`;
        }
        efqString += (item.type === 'refine' ? ' AND ' : ' NOT ') + efqItem;
    });
    const efqStringOri = efqString.replace(/^ AND /, '').trim();
    return efqStringOri;
}
function genRefreshKey(type, folderId, efqid, qid, salt) {
    return `filter[${type}]=${salt}_${folderId}_${efqid || ''}_${qid || ''}`;
}
export const RefineServiceSymbol = Symbol.for('RefineService');
let RefineService = class RefineService extends Service {
    static { RefineService_1 = this; }
    constructor(detailService, smartFilterRepository) {
        super();
        this.detailService = detailService;
        this.smartFilterRepository = smartFilterRepository;
        this.type = '';
        this.currentRefreshKey = '';
        this.rankingConfs = {};
        this.initDataResp = null;
        this.ready = false;
        this.displayFilterList = [];
        this.preOpenStatusMap = {};
        this.flag = 0;
        // inversify 对于基类的注入，需要特殊处理
        this.subscriptions = new Set();
        this.services = new Set();
        this.initialize();
    }
    get query() {
        // const search = window.location.search.substring(1);
        return this.detailService.route.query;
    }
    getRefreshKeyFromRoute() {
        // refreshSalt “_” 参数的意义见 routers.ts -> refreshWithParamsAsQuery
        const query = this.query; // 必须是detailService.route，否则不是响应式
        const refreshSalt = query._ || '';
        return genRefreshKey(this.type, query.folderId, query.efqid || '', query.qid || '', refreshSalt);
    }
    updateCurrentRefreshKey() {
        const p = this.ajaxConf.params;
        const query = this.query;
        const refreshSalt = query._ || '';
        return genRefreshKey(this.type, p.folder_id, p.efq || '', p.qid || '', refreshSalt);
    }
    initialize(ajaxConf) {
        if (ajaxConf) {
            this.ajaxConf = ajaxConf;
            this.type = ajaxConf._type;
        }
        this.ready = false;
        this.preOpenNth = 2;
        this.preOpenStatusMap = {};
        this.displayFilterList = [];
        this.rankingConfs = {
            rankingData: {},
            collapseItems: [],
            externalFieldsQueries: [],
            fieldTypeMap: {},
        };
        this.sortConfig = {
            displayList: [],
            hiddenList: [],
            collapseItems: [],
        };
    }
    get i18n_namespace() {
        const fieldI18nKey = get(this.detailService, 'viewDataKeyConfig.fieldI18nKey');
        // patent使用filterFields
        return fieldI18nKey !== 'commonPatent' ? `${fieldI18nKey}.BASIC` : 'commonPatent.filterFields';
    }
    get isBio() {
        return this.type === 'workspace-BIO';
    }
    static hasRankingDataPreparedByFieldKey(d, fieldKey) {
        return get(d, `${fieldKey}.field`) === fieldKey && Array.isArray(get(d, `${fieldKey}.value`));
    }
    static { this.externalFieldsQueries2EfqString = externalFieldsQueries2EfqString; }
    async init(ajaxConf) {
        this.initialize(ajaxConf);
        try {
            const params = cloneDeep(this.ajaxConf.params);
            this.flag++;
            const flag = this.flag;
            const promised = [leftFilterInitApi(params)];
            const sfqList = _.compact(params.sfq?.split(','));
            if (sfqList?.length) {
                promised.push(this.smartFilterRepository.getSmartFilterResults(sfqList));
            }
            const [initDataResp, smartFilterResults] = await Promise.all(promised);
            if (flag !== this.flag) {
                return;
            }
            this.initDataResp = initDataResp;
            const i18n = injectService(i18nSymbol);
            const oriMessages = i18n.getLocaleMessage(i18n.locale);
            const appendLocal = {};
            // @ts-ignore
            get(this.initDataResp, 'filterConfigs', []).forEach((filterConfig) => {
                // 组件里写死了用commonPatent.filterFields, 所以需要把paper的文案塞到fullback文案中一份;
                if (!this.detailService.isPatentsDetailType) {
                    const { field } = filterConfig;
                    set(appendLocal, `commonPatent.filterFields.${field}`, i18n.t(`${this.i18n_namespace}.${field}`));
                }
                if (filterConfig.field && filterConfig.name) {
                    set(appendLocal, `${this.i18n_namespace}.${filterConfig.field}`, filterConfig.name);
                }
            });
            // @ts-ignore
            get(this.initDataResp, 'efq', []).forEach((externalField) => {
                const { field, childField } = externalField;
                const key = childField ? `${this.i18n_namespace}.groups.${childField}` : `${this.i18n_namespace}.${field}`;
                const otherConfigs = get(this, 'initDataResp.otherConfigs') || {};
                // @ts-ignore
                const fullbackI18n = get(find(otherConfigs, { field: childField || field }), 'name');
                if (!i18n.te(key) && fullbackI18n) {
                    set(appendLocal, key, fullbackI18n);
                }
            });
            const { setSmartFilterResults, } = useSmartFilter();
            // smart filter 时 隐藏 pn 字段
            if (sfqList?.length && smartFilterResults?.length) {
                setSmartFilterResults(smartFilterResults);
                get(this.initDataResp, 'efq', []).forEach((e) => {
                    if (e.field === PN_STR) {
                        e.hidden = true;
                    }
                });
            }
            else {
                setSmartFilterResults([]);
            }
            i18n.setLocaleMessage(i18n.locale, merge(oriMessages, appendLocal));
            this.updateFieldTypeMap();
            this.updateRankingStatus();
            this.updateCollapseItems();
            this.updateCurrentRefreshKey();
            await this.postProcessCollapseItems();
            this.ready = true;
        }
        catch (err) {
            console.log(err);
            this.ready = err;
        }
        return this.initDataResp;
    }
    handleToggleItem({ collapseItem, isOpened }) {
        this.preOpenStatusMap[collapseItem.key] = isOpened;
        const _collapseItem = _.find(this.rankingConfs.collapseItems, { key: collapseItem.key });
        if (_collapseItem) {
            _collapseItem.isOpened = isOpened;
        }
    }
    // 顶上的正在过滤展示
    updateRankingStatus() {
        if (!get(this, 'initDataResp.efq')) {
            return;
        }
        const efq = map(get(this, 'initDataResp.efq', []), item => {
            // @ts-ignore
            const filterConfigItem = _.find(get(this, 'initDataResp.filterConfigs', []), { field: item.field }) || {};
            return {
                //@ts-ignore
                ...item,
                context: filterConfigItem,
            };
        });
        this.rankingConfs.externalFieldsQueries = [...efq];
    }
    updateFieldSort(option) {
        const { displayList, hiddenList, displayFilterList } = option;
        this.displayFilterList = displayFilterList;
        this.sortConfig = {
            ...this.sortConfig,
            displayList,
            hiddenList,
        };
        this.updateCollapseItems();
        this.postProcessCollapseItems();
    }
    updateFieldSortConfig() {
        const displayList = [];
        const hiddenList = [];
        const collapseItems = this.updateCollapseItems(true);
        forEach(collapseItems, (item) => {
            const { key, context, title } = item;
            const sortItem = {
                field: key,
                displayName: title,
                fieldColor: context.fieldColor,
                fieldLabelScope: context.fieldLabelScope,
            };
            const index = this.displayFilterList.indexOf(key);
            if (index > -1) {
                displayList[index] = sortItem;
            }
            else {
                hiddenList.push(sortItem);
            }
        });
        this.sortConfig = {
            displayList: filter(displayList),
            hiddenList,
            collapseItems,
        };
    }
    async getUnitConvertData(data = {}) {
        return await unitConvertApi(data)
            .then(res => {
            // @ts-ignore
            return res.data || {};
        });
    }
    async getMeasurementData(paramsOverwrites = {}) {
        const params = cloneDeep({
            desc: true,
            ...this.ajaxConf.params,
            field: 'PUM_PROP_ID',
            q: this.initDataResp.q,
            ...paramsOverwrites,
        });
        // q不存在时需要删除
        if (!params.q)
            delete params.q;
        delete params.qid; // 要的是q，不是qid
        return await measurementApi(params)
            .then(res => {
            // @ts-ignore
            return res.data || {};
        });
    }
    // paramsOverwrites 为额外参数使用 目前有benefit_tp_id
    async getRankingData(fieldKey, paramsOverwrites = {}) {
        const params = cloneDeep({
            desc: true,
            ...this.ajaxConf.params,
            field: fieldKey,
            q: this.initDataResp.q,
            ...paramsOverwrites,
        });
        // q不存在时需要删除
        if (!params.q)
            delete params.q;
        delete params.qid; // 要的是q，不是qid
        return await getLeftFilterRankingApi(params)
            .then(r => {
            const rankingsData = get(r, 'data.data');
            if (Array.isArray(get(rankingsData, '[0].value'))) {
                const filterData = filterRankingData(rankingsData);
                return filterData[0];
            }
            throw new Error('Field data error');
        });
    }
    updateCollapseItems(transform = false) {
        if (!(this.initDataResp && this.initDataResp.filterConfigs)) {
            this.ready = new Error('InitData not valid');
            return;
        }
        const i18n = injectService(i18nSymbol);
        // @ts-ignore
        let collapseItems = formatLeftFilterConfig(this.initDataResp);
        collapseItems = collapseItems.map((item, index) => {
            let title = get(item, 'context.name', '');
            const fieldType = get(item, 'context.fieldType', '');
            let selectedCount = 0;
            if (!title) {
                title = i18n.te(`${this.i18n_namespace}.${item.key}`) ? i18n.t(`${this.i18n_namespace}.${item.key}`) : item.key;
            }
            if (fieldType === 'OPTION_COLOUR_LAB') {
                title = i18n.t(`${this.i18n_namespace}.PATENT_COLOUR`);
            }
            const tooltip = TOOLTIP_FIELDS[item.key];
            const { externalFieldsQueries } = this.rankingConfs;
            forEach(externalFieldsQueries, (queryItem) => {
                const { field, childField, type } = queryItem;
                let key = field;
                if (['IPC_FACET', 'CPC_FACET', 'LOC_FACET', 'UPC_FACET'].indexOf(field) > -1) {
                    key = 'CLASSIFICATION_TYPE';
                }
                if (key === 'MIPC_FACET') {
                    key = 'MIPC';
                }
                if ((key === item.key || childField === item.key) && type === 'refine') {
                    selectedCount += get(queryItem, 'value.length', 0);
                    // 专利价值不用显示选中的数量，见TOC-32577
                    if (FIELD_TYPES[key] === BASIC_TYPE.range) {
                        selectedCount = 0;
                    }
                }
            });
            return { ...item, title, tooltip, selectedCount };
        });
        if (transform) {
            return collapseItems;
        }
        this.rankingConfs.collapseItems = collapseItems;
    }
    async postProcessCollapseItems() {
        if (!(this.initDataResp && this.initDataResp.filterConfigs && this.ajaxConf.cacheFilterSortConfig)) {
            return;
        }
        if (!this.displayFilterList.length) {
            if (!this.detailService.isPatentsDetailType) {
                // PAPER 显示全部字段
                this.displayFilterList = map(this.initDataResp.filterConfigs, ({ field }) => field);
            }
            else {
                try {
                    this.displayFilterList = await this.ajaxConf.cacheFilterSortConfig.getFilterSort();
                }
                catch (err) {
                    this.displayFilterList = await this.ajaxConf.cacheFilterSortConfig.getDefaultFilterSort();
                }
            }
            this.updateFieldSortConfig();
        }
        const getFiledKey = (cm) => get(cm, 'context.field');
        this.rankingConfs.collapseItems = this.rankingConfs.collapseItems
            .filter(o => {
            return this.displayFilterList.indexOf(getFiledKey(o)) > -1;
        })
            .sort((a, b) => this.displayFilterList.indexOf(getFiledKey(a)) - this.displayFilterList.indexOf(getFiledKey(b)));
        this.rankingConfs.collapseItems = this.rankingConfs.collapseItems.map((item, index) => {
            let isOpened = index < this.preOpenNth;
            if (this.isBio) {
                // bio的过滤项全展开
                isOpened = true;
            }
            else {
                if (this.preOpenNth < 0) {
                    isOpened = this.preOpenStatusMap[item.key] || false;
                }
            }
            return { ...item, isOpened };
        });
        this.preOpenNth = -Infinity;
    }
    updateFieldTypeMap() {
        this.rankingConfs.fieldTypeMap = FIELD_TYPES;
        const otherConfigs = get(this, 'initDataResp.otherConfigs', {});
        // @ts-ignore
        if (!otherConfigs.length)
            return;
        // 自定义字段过滤项
        // @ts-ignore
        otherConfigs.forEach(otherField => {
            const { field = '' } = otherField;
            if (field.startsWith('WS_LF')) {
                this.rankingConfs.fieldTypeMap[otherField.field] = BASIC_TYPE.layer;
            }
            if (field.startsWith('WS_CF')) {
                this.rankingConfs.fieldTypeMap[otherField.field] = BASIC_TYPE.default;
            }
        });
    }
    async updateSearchRanking(fieldKey) {
        if (RefineService_1.hasRankingDataPreparedByFieldKey(this.rankingConfs.rankingData, fieldKey)) {
            // 去掉这个会无限循环
            return;
        }
        try {
            const fieldData = await this.getRankingData(fieldKey);
            this.rankingConfs.rankingData = {
                ...this.rankingConfs.rankingData,
                [fieldKey]: fieldData,
            };
        }
        catch (e) {
            this.rankingConfs.rankingData = {
                ...this.rankingConfs.rankingData,
                [fieldKey]: {
                    field: fieldKey,
                    value: [],
                },
            };
        }
    }
    cleanCachedRankingByField(fieldKey) {
        delete this.rankingConfs.rankingData[fieldKey];
    }
};
RefineService = RefineService_1 = __decorate([
    provide(RefineServiceSymbol),
    __param(0, inject(DetailServiceSymbol)),
    __param(1, inject(SmartFilterRepository)),
    __metadata("design:paramtypes", [Object, typeof (_a = typeof SmartFilterRepository !== "undefined" && SmartFilterRepository) === "function" ? _a : Object])
], RefineService);
export { RefineService };
